import { Button, Stack } from '@mui/material';
import { generatePath, useSearchParams } from 'react-router-dom';

import { AppDrawer, AppDrawerHeader, MaterialIcon } from '@components';

import { getFragmentData } from '@api/gql/fragment-masking';

import {
  ASSESSMENT_REPORT_LINKED_ELEMENTS_FILTER,
  ASSESSMENT_REPORT_PERMISSIONS_FILTER,
  ASSESSMENT_REPORT_RESOURCES_FILTER,
  FORCE_REVOKE_PARAM,
  REPORT_ITEM_PARAM,
  ASSESSMENT_REPORT_TIER_FILTER,
} from '@StandingAccess/types';
import {
  useGetStandingAccessReportEntitlementGroup,
  useGetStandingAccessReportUsages,
} from '@StandingAccess/services/standingAccessQueries';
import { standingAccessReportUsageFragmentDocument } from '@StandingAccess/services/gql';
import AssessmentReportDrawerTable from '@StandingAccess/components/AssessmentReportDrawerTable';
import { useReportFilters } from '@StandingAccess/hooks/use-report-filters';
import AssessmentReportDrawerGroupInfo from '@StandingAccess/components/AssessmentReportDrawerGroupInfo';
import AssessmentReportLinkedElementsFilter from './AssessmentReportLinkedElementsFilter';
import { useMemo, useState } from 'react';
import { RouterLink } from '@libs';
import { routes } from '@routes';
import { useFlagFixed } from '@hooks';
import { Flag } from '@utils/feature-flags';

interface AssessmentReportDrawerContentProps {
  reportId: string;
  groupCursor: string;
}

function parseGroupCursor(groupCursor: string) {
  const [resourceId, permissionId] = groupCursor.split(':');

  if (!resourceId || !permissionId) {
    return {};
  }

  return { resourceId, permissionId };
}

function AssessmentReportDrawerRevokeActions({
  reportId,
  groupCursor,
  selectedAssignmentTypes,
  onSelectAssignmentTypes,
}: {
  reportId: string;
  groupCursor: string;
  selectedAssignmentTypes: string[];
  onSelectAssignmentTypes: (value: string[]) => void;
}) {
  const { resourceId, permissionId } = parseGroupCursor(groupCursor);
  const { tier } = useReportFilters();

  const revokePath = useMemo(() => {
    const revokeSP = [
      `${ASSESSMENT_REPORT_RESOURCES_FILTER.param}=${resourceId}`,
      `${ASSESSMENT_REPORT_PERMISSIONS_FILTER.param}=${permissionId}`,
      `${FORCE_REVOKE_PARAM}=true`,
    ];

    if (tier?.length) {
      tier.forEach((t) => {
        revokeSP.push(`${ASSESSMENT_REPORT_TIER_FILTER.param}=${t}`);
      });
    }

    if (selectedAssignmentTypes.length > 0) {
      selectedAssignmentTypes.forEach((type) => {
        revokeSP.push(`${ASSESSMENT_REPORT_LINKED_ELEMENTS_FILTER.param}=${type}`);
      });
    }

    return `${generatePath(routes.StandingAccessReportRevoke.path, {
      id: reportId,
    })}?${revokeSP.join('&')}`;
  }, [permissionId, reportId, resourceId, selectedAssignmentTypes, tier]);

  return (
    <Stack direction="row" justifyContent="space-between" spacing={2}>
      <AssessmentReportLinkedElementsFilter
        label="Assignment Type"
        reportId={reportId}
        selected={selectedAssignmentTypes}
        filter={{
          resource: resourceId ? [resourceId] : [],
          permission: permissionId ? [permissionId] : [],
          tier,
        }}
        onSelect={onSelectAssignmentTypes}
      />
      {selectedAssignmentTypes.length > 0 && (
        <Button
          component={RouterLink}
          to={revokePath}
          variant="contained"
          color="primary"
          endIcon={<MaterialIcon symbol="arrow_right_alt" />}
        >
          Revoke
        </Button>
      )}
    </Stack>
  );
}

function AssessmentReportDrawerContent({ reportId, groupCursor }: AssessmentReportDrawerContentProps) {
  const { isEnabled: isAccessFlowEnabled } = useFlagFixed(Flag.ASSESSMENT_REPORTS_ACCESS_FLOW);

  const filter = useReportFilters();
  const [selectedAssignmentTypes, setSelectedAssignmentTypes] = useState<string[]>([]);

  const { data: entitlementGroup } = useGetStandingAccessReportEntitlementGroup({
    groupCursor,
  });

  const { data, isFetched, fetchNextPage, isFetchingNextPage } = useGetStandingAccessReportUsages({
    reportId,
    groupCursor,
    filter: {
      ...filter,
      linkedElement: selectedAssignmentTypes,
    },
  });

  const totalCount = data?.pages[0].data.totalCount ?? 0;
  const usages =
    data?.pages.flatMap((page) =>
      page.data.edges.map((edge) => getFragmentData(standingAccessReportUsageFragmentDocument, edge.node)),
    ) ?? [];

  return (
    <Stack direction="column" spacing={2}>
      {entitlementGroup && <AssessmentReportDrawerGroupInfo entitlementGroup={entitlementGroup} />}
      <AssessmentReportDrawerRevokeActions
        reportId={reportId}
        groupCursor={groupCursor}
        selectedAssignmentTypes={selectedAssignmentTypes}
        onSelectAssignmentTypes={setSelectedAssignmentTypes}
      />
      <AssessmentReportDrawerTable
        isFetching={!isFetched}
        totalCount={totalCount}
        usages={usages}
        onLoadMore={fetchNextPage}
        isLoadingMore={isFetchingNextPage}
        selectedConnections={selectedAssignmentTypes}
        onSelectConnection={(val) => setSelectedAssignmentTypes([val])}
        withAccessFlow={isAccessFlowEnabled}
      />
    </Stack>
  );
}

interface AssessmentReportDrawerProps {
  reportId: string;
}

export default function AssessmentReportDrawer({ reportId }: AssessmentReportDrawerProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const reportItemId = searchParams.get(REPORT_ITEM_PARAM);

  const handleClose = () => {
    searchParams.delete(REPORT_ITEM_PARAM);
    setSearchParams(searchParams, { replace: true });
  };

  return (
    <AppDrawer
      open={!!reportItemId}
      onClose={handleClose}
      width={800}
      header={<AppDrawerHeader title="Assignments" onCloseClick={handleClose} />}
    >
      {reportItemId && <AssessmentReportDrawerContent reportId={reportId} groupCursor={reportItemId} />}
    </AppDrawer>
  );
}
